import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import { TASK_QUEUE_POLL_INTERVAL_MS } from './useTaskQueue';
import type { CreateTaskTypeInput, UpdateTaskTypeInput } from '@erp/rpc';
import {
  ACTIVITY_PAGE_SIZE,
  flattenActivityPages,
  nextActivityOffset,
} from 'src/api/rpc/hooks/activityFeedPaging';

/**
 * Hooks over the org's task type registry (`taskTypes.*`). Reads are shared
 * across the queue page, detail page, and the settings admin card; mutations
 * invalidate the list so every consumer refreshes together.
 */

function useInvalidateTaskTypes() {
  const queryClient = useQueryClient();
  return function invalidateTaskTypes() {
    void queryClient.invalidateQueries({
      queryKey: orpc.taskTypes.list.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.taskTypes.get.key(),
    });
    // A type's chosen metric cards change what the Task Page strip shows.
    void queryClient.invalidateQueries({
      queryKey: orpc.taskTypes.metrics.key(),
    });
    // Every save adds to the type's Activity timeline.
    void queryClient.invalidateQueries({
      queryKey: orpc.taskTypes.listActivity.key(),
    });
  };
}

export function useTaskTypes(options?: {
  enabled?: boolean;
  /** Also return archived types (e.g. for tasks that outlived their type). */
  includeArchived?: boolean;
}) {
  return useQuery({
    ...orpc.taskTypes.list.queryOptions({
      input: options?.includeArchived ? { includeArchived: true } : {},
    }),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

export function useTaskType(id: string | null | undefined) {
  return useQuery({
    ...orpc.taskTypes.get.queryOptions({ input: { id: id ?? '' } }),
    enabled: !!id,
  });
}

/** Option names of a task type's outcome Core Enum; empty when it has none. */
export function useTaskTypeOutcomeOptions(enumId: string | null | undefined) {
  return useQuery({
    ...orpc.taskTypes.getOutcomeOptions.queryOptions({
      input: { enumId: enumId ?? '' },
    }),
    enabled: !!enumId,
  });
}

export function useCreateTaskType() {
  const invalidateTaskTypes = useInvalidateTaskTypes();
  return useMutation({
    mutationFn: (input: CreateTaskTypeInput) =>
      rpcClient.taskTypes.create(input),
    onSuccess: invalidateTaskTypes,
  });
}

export function useUpdateTaskType() {
  const invalidateTaskTypes = useInvalidateTaskTypes();
  return useMutation({
    mutationFn: (input: UpdateTaskTypeInput) =>
      rpcClient.taskTypes.update(input),
    onSuccess: invalidateTaskTypes,
  });
}

export function useArchiveTaskType() {
  const invalidateTaskTypes = useInvalidateTaskTypes();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => rpcClient.taskTypes.archive({ id }),
    onSuccess: invalidateTaskTypes,
  });
}

export function useGenerateTaskTypeStageTree() {
  // Generate returns a draft; nothing is persisted until applyStageTree.
  // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation
  return useMutation({
    mutationFn: rpcClient.taskTypes.generateStageTree,
  });
}

export function useExportTaskTypeBundle() {
  // Export only reads; nothing to invalidate.
  // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation
  return useMutation({
    mutationFn: rpcClient.taskTypes.exportBundle,
  });
}

export function useImportTaskTypeBundle() {
  const invalidateTaskTypes = useInvalidateTaskTypes();
  return useMutation({
    mutationFn: rpcClient.taskTypes.importBundle,
    onSuccess: (result) => {
      // A dry run writes nothing.
      if (!result.dryRun) invalidateTaskTypes();
    },
  });
}

export function useApplyTaskTypeStageTree() {
  const invalidateTaskTypes = useInvalidateTaskTypes();
  return useMutation({
    mutationFn: rpcClient.taskTypes.applyStageTree,
    onSuccess: invalidateTaskTypes,
  });
}

/**
 * Recent playbook health for a task type (`taskTypes.getProcedureHealth`):
 * run success, pull rates, waivers, and library pulls the type does not bind.
 */
/** A task type's metric cards over the open tasks the caller can see. */
export function useTaskTypeMetrics(
  taskTypeId: string | null | undefined,
  excludeSnoozed = false,
  /** False skips the request, e.g. for a type with no cards. */
  enabled = true,
) {
  return useQuery({
    ...orpc.taskTypes.metrics.queryOptions({
      input: { taskTypeId: taskTypeId ?? '', excludeSnoozed },
    }),
    enabled: enabled && !!taskTypeId,
    staleTime: 30 * 1000,
    // Poll with the queue below the cards so counts and sums don't drift
    // from the rows while the page stays open.
    refetchInterval: TASK_QUEUE_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useTaskTypeProcedureHealth(
  taskTypeId: string | null | undefined,
  days = 30,
) {
  return useQuery({
    ...orpc.taskTypes.getProcedureHealth.queryOptions({
      input: { taskTypeId: taskTypeId ?? '', days },
    }),
    enabled: !!taskTypeId,
    staleTime: 60 * 1000,
  });
}

/**
 * A task type's audit trail (newest first), paged by offset so the Activity
 * section can "Load more" without refetching what it already shows.
 */
export function useTaskTypeActivity(
  taskTypeId: string | null | undefined,
  enabled = true,
) {
  const query = useInfiniteQuery({
    ...orpc.taskTypes.listActivity.infiniteOptions({
      input: (pageParam: number) => ({
        taskTypeId: taskTypeId ?? '',
        limit: ACTIVITY_PAGE_SIZE,
        offset: pageParam,
      }),
      initialPageParam: 0,
      getNextPageParam: nextActivityOffset,
    }),
    enabled: enabled && !!taskTypeId,
  });

  return { ...query, ...flattenActivityPages(query.data) };
}
